<template>
    <div class="editable-field" ref="container">
        <span v-if="isEditing && editEnabled" class="d-flex align-items-center">
            <!-- Regular Text -->
            <textarea
                v-if="editorType == 'text'"
                v-model="editedValue"
                type="text"
                :placeholder="placeholder"
                :class="[editorClass]"
                class="form-control py-1"
                @input="autoExpandTextarea"
                @blur="handleBlur($event)"
                rows="1"
                ref="textinput"
            ></textarea>
            <!-- Rich Text -->
            <rich-text
                v-else-if="editorType == 'richtext'"
                v-model="editedValue"
                :placeholder="placeholder"
                :class="[editorClass]"
                @blur="handleBlur($event)"
                ref="richtextinput"
            ></rich-text>
            <button class="btn btn-icon ml-2 p-1" @click="closeEdit()">
                <span class="icon icon-glyphicons-basic-check text-primary"></span>
            </button>
        </span>
        <!-- Empty Placeholder Text -->
        <span v-else-if="!isEditing && editEnabled && !value" @click="enterEdit()" class="text-secondary">
            {{ emptyPlaceholder }}</span
        >
        <!-- Text Display (non-edit mode)-->
        <span v-else-if="value" class="d-flex align-items-center">
            <span
                v-if="editorType == 'richtext'"
                v-html="value"
                class="rich-text-content"
                :class="{ pointer: editEnabled && !isEditing }"
                @click="enterEdit()"
            ></span>
            <span
                v-else
                class="mb-0 whitespace-pre"
                :class="{ pointer: editEnabled && !isEditing }"
                @click="enterEdit()"
                >{{ value }}</span
            >
            <button class="btn btn-icon ml-2 p-1 edit-btn" @click="enterEdit()" v-show="editEnabled">
                <span class="icon icon-lg icon-glyphicons-basic-pencil"></span>
            </button>
        </span>
    </div>
</template>

<script>
import RichText from "@/components/RichText"

export default {
    name: "EditableTextField",
    components: {
        RichText,
    },
    props: {
        value: {
            type: String,
            required: false,
        },
        editorType: {
            type: String,
            required: false,
            default: "text",
        },
        placeholder: {
            type: String,
            required: false,
            default: "",
        },
        editorClass: {
            type: String,
            required: false,
            default: "",
        },
        editEnabled: {
            type: Boolean,
            required: false,
            default: true,
        },
        emptyPlaceholder: {
            type: String,
            required: false,
            default() {
                return "Add some text..."
            },
        },
    },
    data() {
        return {
            isEditing: false,
            editedValue: this.value,
        }
    },
    methods: {
        // Enter edit mode.
        enterEdit() {
            if (!this.editEnabled) {
                return
            }
            this.editedValue = this.value
            this.isEditing = true
            let vm = this
            this.$nextTick(() => {
                vm.autoExpandTextarea()
                if (vm.editorType === "text") {
                    vm.$refs.textinput.focus()
                } else if (vm.editorType === "richtext") {
                    vm.$refs.richtextinput.focus()
                }
            })
        },
        // Close edit mode.
        closeEdit() {
            this.isEditing = false
        },
        // Expand the height of a textarea input to fit its contents.
        autoExpandTextarea(event) {
            const input = event ? event.target : this.$refs.textinput
            if (input) {
                // Reset the height to shrink back if text is deleted.
                input.style.height = "auto"
                // Set the height to the scrollHeight to remove the scrollbar.
                input.style.height = input.scrollHeight + "px"
            }
        },
        // Close edit mode when the user re-focusses on an element outside the component.
        handleBlur(event) {
            if (!event.relatedTarget) {
                this.isEditing = false
            }
            if (!this.$refs.container.contains(event.relatedTarget)) {
                this.isEditing = false
            }
        },
    },
    watch: {
        editedValue: function (newValue) {
            this.$emit("input", newValue)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.editable-field {
    .edit-btn {
        opacity: 0;
    }
    &:hover {
        .edit-btn {
            opacity: 1;
        }
    }
}
</style>

<style lang="scss">
.rich-text-content {
    h1 {
        font-size: 1.5rem;
    }
    h2 {
        font-size: 1.25rem;
    }
    h3 {
        font-size: 1rem;
    }
}
</style>